import localhost, {
  downloadWithIFrame,
  downloadFileFromResponse,
} from "apis/localhost";
import { showErrorModal } from "slices/app/actions/error";
import { getErrorMessage } from "utils";
import { modalTypes } from "constants/types";
import { formatRequest, formatResponse } from "utils/dictionary";
import { hideModal } from "slices/app/actions/modals";
import { DICTIONARIES_HOME, getDictionaryPath } from "constants/routes";
import {
  hideInlineAlert,
  showGlobalAlert,
  showInlineAlert,
  disableNavBarAndSideBar,
  addToJobQueue,
  clearJobQueue,
} from "slices/app/appSlice";
import { alertVariants } from "@visiblethread/react-components/dist/components/Alert";
import {
  DICTIONARY_NAME_ALREADY_EXIST,
  DICTIONARY_NAME_INVALID_CHARACTERS,
  dictionaryNotFound,
  FILE_SIZE_INVALID,
  hasBeenAlreadyDeletedError,
  menuOptionLockError,
} from "constants/dictionary";
import { getDictionaryName } from "slices/dictionary/helpers";
import {
  setLocked,
  setModeToEdit,
  setModeToView,
  cancelEdits,
  setDictionary,
  getDictionaries,
} from "slices/dictionary/dictionarySlice";

const MULTIPLE_DICTIONARIES = "multipart/form-data";

const handleMenuOptionLockError = (dispatch) => {
  dispatch(setLocked(true));
  dispatch(
    showInlineAlert({
      variant: alertVariants.warning,
      text: menuOptionLockError,
    })
  );
};

export const cancelChanges = () => (dispatch) => {
  dispatch(cancelEdits());
  dispatch(setModeToView());
  dispatch(disableNavBarAndSideBar(false));
};

export const openEditMode = () => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));

  const { id } = getState().dictionaries.edit.editDictionary;
  await localhost
    .get("/docs/dictionary/lock", {
      params: {
        id,
      },
    })
    .then(() => {
      dispatch(setModeToEdit());
      dispatch(hideInlineAlert());
      dispatch(disableNavBarAndSideBar(true));
    })
    .catch((error) => {
      const { errorCode, message } = error.response.data;
      if (errorCode === "DICTIONARY_LOCKED") {
        dispatch(setLocked(true));
        dispatch(
          showInlineAlert({
            variant: alertVariants.warning,
            text: message,
          })
        );
      } else if (errorCode === "DICTIONARY_NOT_FOUND") {
        dispatch(
          showInlineAlert({
            variant: alertVariants.danger,
            text: hasBeenAlreadyDeletedError,
          })
        );
      } else {
        dispatch(showErrorModal(error));
      }
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const closeEditMode = () => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));

  const { id } = getState().dictionaries.edit.unchangedDictionary;
  await localhost
    .get("/docs/dictionary/unlock", {
      params: {
        id,
      },
    })
    .then(() => {
      dispatch(cancelChanges());
      dispatch(setModeToView());
      dispatch(disableNavBarAndSideBar(false));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const fetchDictionary = (dictionaryId, navigate) => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/docs/dictionary", {
      params: { dictionaryId },
    })
    .then((response) => {
      const { data } = response;
      // eslint-disable-next-line no-undef
      if (Number(UserInfo.workspaceId) !== data.workspaceId) {
        dispatch(
          showGlobalAlert({
            variant: alertVariants.warning,
            text: dictionaryNotFound,
          })
        );
        navigate(DICTIONARIES_HOME);
        return;
      }
      dispatch(setDictionary(formatResponse(data)));
    })
    .catch((error) => {
      if (error.response.status === 404) {
        dispatch(
          showGlobalAlert({
            variant: alertVariants.warning,
            text: dictionaryNotFound,
          })
        );
        navigate(DICTIONARIES_HOME);
      } else {
        dispatch(showErrorModal(error));
      }
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const fetchDictionaries = () => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/docs/dictionary/list", {})
    .then((response) => {
      const { data } = response;
      dispatch(getDictionaries(data));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};
export const saveEdit = () => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));

  const { editDictionary } = getState().dictionaries.edit;
  await localhost
    .put(`/docs/dictionary`, formatRequest(editDictionary))
    .then((response) => {
      const { data } = response;
      let formattedDictionary = formatResponse(data);
      formattedDictionary = {
        ...formattedDictionary,
        categories: formattedDictionary.categories.map(
          (category, categoryIndex) => ({
            ...category,
            isExpanded:
              editDictionary?.categories[categoryIndex]?.isExpanded || false,
          })
        ),
      };
      dispatch(setDictionary(formattedDictionary));
      dispatch(setModeToView());
      dispatch(disableNavBarAndSideBar(false));
    })
    .catch((error) => {
      const { errorCode, message } = error.response.data;
      if (
        errorCode === DICTIONARY_NAME_INVALID_CHARACTERS ||
        errorCode === DICTIONARY_NAME_ALREADY_EXIST ||
        errorCode === FILE_SIZE_INVALID
      ) {
        dispatch(
          showGlobalAlert({
            variant: alertVariants.danger,
            text: message,
          })
        );
      } else {
        dispatch(showErrorModal(error));
      }
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const saveNew = (navigate) => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));

  const {
    editDictionary,
    editDictionary: { categories },
  } = getState().dictionaries.edit;
  await localhost
    .post(`/docs/dictionary`, formatRequest(editDictionary))
    .then((response) => {
      const { data } = response;
      dispatch(setModeToView());
      dispatch(disableNavBarAndSideBar(false));

      const expandCollapseSettings = categories.map(
        (category) => category.isExpanded
      );
      navigate(getDictionaryPath(data.id), {
        state: { expandCollapseSettings },
      });
    })
    .catch((error) => {
      const { errorCode, message } = error.response.data;
      if (
        errorCode === DICTIONARY_NAME_INVALID_CHARACTERS ||
        errorCode === FILE_SIZE_INVALID
      ) {
        dispatch(
          showGlobalAlert({
            variant: alertVariants.danger,
            text: message,
          })
        );
      } else {
        dispatch(showErrorModal(error));
      }
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const saveChanges = (navigate) => (dispatch, getState) => {
  const { editDictionary } = getState().dictionaries.edit;
  if (editDictionary.id) {
    dispatch(saveEdit());
  } else {
    dispatch(saveNew(navigate));
  }
};

export const deleteDictionary =
  (dictionaryId, navigate) => async (dispatch, getState) => {
    dispatch(addToJobQueue("genericJob"));

    const { name } = getState().dictionaries.edit.editDictionary;

    await localhost
      .delete("/docs/dictionary/edit/delete", {
        params: { dictionaryId },
      })
      .then((response) => {
        const { data } = response;
        dispatch(getDictionaries(data));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: `"${name}" successfully deleted`,
          })
        );
        navigate(DICTIONARIES_HOME);
      })
      .catch((error) => {
        if (error.response.data.errorCode === "DICTIONARY_LOCKED") {
          handleMenuOptionLockError(dispatch);
        } else {
          dispatch(showErrorModal(error));
        }
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const createDictonary = () => (dispatch, getState) => {
  const { dictionaryList } = getState().dictionaries;
  // eslint-disable-next-line no-undef
  const { username, workspaceId } = UserInfo;
  const dictionary = {
    workspaceId,
    updatedBy: username,
    createdBy: username,
    type: "Quality Practices",
    name: getDictionaryName(dictionaryList),
    comment: `created by ${username}`,
    categories: [
      {
        name: "Default Category",
        description: "",
        terms: [],
        isExpanded: true,
      },
    ],
  };

  dispatch(setDictionary(formatResponse(dictionary)));
  dispatch(setModeToEdit());
  dispatch(disableNavBarAndSideBar(true));
};

export const importDictionaryFromArchive =
  (formData, navigate, fileExtension) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .post(`/docs/dictionary/import/${fileExtension}`, formData, {
        headers: {
          "Content-Type": MULTIPLE_DICTIONARIES,
        },
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(setDictionary(formatResponse(data)));
        dispatch(hideModal(modalTypes.IMPORT_DICTIONARY));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: `"${
              formData.get("importedFile").name
            }" imported successfully`,
          })
        );
        navigate(getDictionaryPath(data.id));
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.IMPORT_FILE));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.danger,
            text: getErrorMessage(error),
          })
        );
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const restoreDictionaries = (formData) => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .post("/docs/dictionary/restore", formData, {
      headers: {
        "Content-Type": MULTIPLE_DICTIONARIES,
      },
    })
    .then(async (response) => {
      const { importedDictionaries, dictionariesToImport, dictionaries } =
        response.data;

      dispatch(getDictionaries(dictionaries));
      dispatch(hideModal(modalTypes.IMPORT_FILE));
      dispatch(
        showGlobalAlert({
          variant:
            importedDictionaries === dictionariesToImport
              ? alertVariants.success
              : alertVariants.warning,
          text: `Restore Dictionaries Complete. We successfully restored ${importedDictionaries}/${dictionariesToImport}`,
        })
      );
    })
    .catch((error) => {
      dispatch(hideModal(modalTypes.IMPORT_FILE));
      dispatch(
        showInlineAlert({
          variant: alertVariants.danger,
          text: getErrorMessage(error),
        })
      );
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const backupAllDictionaries = () => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .post("/docs/dictionary/backupAll")
    .then(async (response) => {
      const { data } = response;
      const url = `/docs/dictionary/backupAll?uniqueFolderName=${data}`;
      downloadWithIFrame(url);
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const cloneDictionary =
  (dictionaryId, dictionaryName) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .post("/docs/dictionary/edit/clone", null, {
        params: { dictionaryId, dictionaryName },
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(setDictionary(formatResponse(data)));
        dispatch(hideModal(modalTypes.TEXT_FIELD_MODAL));
      })
      .catch((error) => {
        if (error.response.data.errorCode === "DICTIONARY_LOCKED") {
          handleMenuOptionLockError(dispatch);
        } else {
          dispatch(showErrorModal(error));
        }
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const setDefaultDictionary =
  (dictionaryId, dictionaryName) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .post("/docs/dictionary/edit/setDefault", null, {
        params: { dictionaryId },
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(hideModal(modalTypes.CONFIRMATION));
        dispatch(getDictionaries(data));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: `${dictionaryName} successfully set as the default dictionary`,
          })
        );
      })
      .catch((error) => {
        if (error.response.data.errorCode === "DICTIONARY_LOCKED") {
          handleMenuOptionLockError(dispatch);
        } else {
          dispatch(showErrorModal(error));
        }
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const exportDictionaryXml =
  (dictionaryId, dictionaryName) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .get("/docs/dictionary/export/xml", {
        responseType: "blob",
        params: { dictionaryId },
      })
      .then(async (response) => {
        const { data } = response;
        downloadFileFromResponse(data, `${dictionaryName}.xml`);
      })
      .catch(async (error) => {
        const data = await error.response.data.text();
        const { errorCode } = JSON.parse(data);
        if (errorCode === "DICTIONARY_LOCKED") {
          handleMenuOptionLockError(dispatch);
        } else {
          dispatch(showErrorModal(error));
        }
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const exportDictionaryCsv =
  (dictionaryId, dictionaryName) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .get("/docs/dictionary/export/csv", {
        responseType: "blob",
        params: { dictionaryId },
      })
      .then(async (response) => {
        const { data } = response;
        downloadFileFromResponse(data, `${dictionaryName}.csv`);
      })
      .catch(async (error) => {
        const data = await error.response.data.text();
        const { errorCode } = JSON.parse(data);
        if (errorCode === "DICTIONARY_LOCKED") {
          handleMenuOptionLockError(dispatch);
        } else {
          dispatch(showErrorModal(error));
        }
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const importDictionaryCsv = (formData) => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .post("/docs/dictionary/update/csv", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then(async (response) => {
      const { data } = response;
      dispatch(setDictionary(formatResponse(data)));
      dispatch(setModeToView());
      dispatch(disableNavBarAndSideBar(false));
      dispatch(hideModal(modalTypes.IMPORT_FILE));
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: `"${formData.get("importedFile").name}" imported successfully`,
        })
      );
    })
    .catch((error) => {
      dispatch(hideModal(modalTypes.IMPORT_FILE));
      dispatch(
        showGlobalAlert({
          variant: alertVariants.danger,
          text: getErrorMessage(error),
        })
      );
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};
